/**
 * CR-7732: export the haulier's call-off list.
 *
 * The file is the list: the same filters, the same columns the viewer left
 * showing, in the same order. A spreadsheet that quietly held more rows — or
 * different ones — than the screen it came from would be worse than none,
 * because nobody would know which of the two to believe.
 *
 * The server writes it (`GET call-off/call-offs/export/`) from the list's own
 * queryset, as a styled Excel file like the Stock Planning export, or a CSV.
 * It is capped: a haulier's book is small, and a request that never returns is
 * not an export.
 */
import { useCallback, useState } from 'react';
import { callOffAPI } from '../../../lib/api';
import { EXPORT_MIME, blobErrorMessage, saveBlob } from '../../../utils/fileDownload';
import { toast } from 'sonner';

/** Beyond this the export stops (mirror of the backend `EXPORT_ROW_CAP`). */
export const EXPORT_ROW_CAP = 2000;

/** The list's exportable column ids, in list order. */
export const EXPORT_COLUMN_IDS = [
  'reference', 'party', 'pickup_date', 'destination', 'fill_percent', 'detail_status',
  'haulage_cost', 'agreed_delivery_date', 'truck_type', 'created_date', 'last_edited',
] as const;

export type ExportFormat = 'xlsx' | 'csv';

type QueryParams = Record<string, string | number>;

/**
 * Download the current call-off list as Excel or CSV.
 *
 * @example
 *   const { exporting, runExport } = useCallOffExport(params, isVisible, totalCount);
 *   runExport('xlsx');
 * @param params - the filters the list is showing, minus paging
 * @param isVisible - the viewer's column choice, so the file matches the list
 * @param totalCount - how many call-offs the list holds, for the empty / cap notices
 * @returns `exporting` and the `runExport(format)` handler
 */
export function useCallOffExport(
  params: QueryParams,
  isVisible: (id: string) => boolean,
  totalCount: number,
) {
  const [exporting, setExporting] = useState(false);

  const runExport = useCallback(async (fileFormat: ExportFormat) => {
    if (totalCount === 0) {
      toast.info('Nothing to export — no call-offs match these filters.');
      return;
    }
    setExporting(true);
    try {
      const response = await callOffAPI.exportCallOffs({
        ...params,
        file_format: fileFormat,
        columns: EXPORT_COLUMN_IDS.filter(isVisible).join(','),
      });
      saveBlob(
        response.data,
        `call-offs-${new Date().toISOString().slice(0, 10)}.${fileFormat}`,
        EXPORT_MIME[fileFormat],
      );
      if (totalCount > EXPORT_ROW_CAP) {
        toast.warning(
          `Exported the first ${EXPORT_ROW_CAP} call-offs. Narrow the filters to export the rest.`,
        );
      } else {
        toast.success(`Exported ${fileFormat.toUpperCase()}`);
      }
    } catch (err: unknown) {
      toast.error(await blobErrorMessage(err, 'Could not export the call-offs. Please try again.'));
    } finally {
      setExporting(false);
    }
  }, [params, isVisible, totalCount]);

  return { exporting, runExport };
}
